// 既存ロール名 (staff.role / JWT user_metadata.role と一致させる)
export const ROLES = {
  ADMIN:   'admin',
  MANAGER: 'manager',
  PATROL:  'patrol',
  STAFF:   'staff',
  // SPEC-RBAC-CONTRACTOR-ALERTS-01 (D-310) S4: 外部委託。クレサポ(巡回・集金・報告)のみ。マネサポ/タナサポ非表示。
  CONTRACTOR: 'contractor',
}

// SPEC-PRIZE-RANKING-STAFF-ACCESS-V1 (D-600): 参照系レポートを見られるロール。
//   ★ここが唯一の真実。ルートのガード(StaffReportRoute)とハブのタイルの出し分けが
//     どちらもこの 1 つを見る。片方だけ書き換わると「見えるのに弾かれる」/
//     「弾かれないのに見えない」が生まれるので、配列を 2 箇所に書かない。
//   ★D-515 の再発防止そのもの(manager にタイルは見えるのにルートは AdminRoute だった)。
export const STAFF_REPORT_ROLES = ['admin', 'manager', 'staff']

// SPEC-COLLECTION-SCHEDULE-MANAGER-ACCESS-01 (D-701): 管理画面のうち「運用を決める人」が入るもの。
//   ★ここが唯一の真実。ルートのガード(ManagerRoute)とハブのタイルの出し分けが
//     どちらもこの 1 つを見る。配列を 2 箇所に書かない。
//   ★D-515(売上予測)と D-701(集金予定表)は同じ形の事故だった:
//     ハブのタイルは manager/contractor に見えているのに、ルートだけ AdminRoute で弾かれる。
//     ロール表を書き写した瞬間に「見えるのに弾かれる」が生まれる。
//   ★ManagerRoute の中身をここへ持ち上げただけで、許可ロールは 1 つも変えていない
//     (['admin','manager'] のまま)。既存の ManagerRoute 全ルートの挙動は不変。
export const MANAGER_ROLES = ['admin', 'manager']

// D-992: admin だけが入る画面(ナイスランド宛 集金伝票 = 対外の金額)。
//   ★ここが唯一の真実。ルートのガード(AdminRoute)とハブのタイルの出し分けがどちらもこの 1 つを見る。
//   ★AdminRoute の中身(['admin'])をここへ持ち上げただけで、許可ロールは変えていない。
export const ADMIN_ONLY_ROLES = ['admin']

// モジュールキー → 許可ロール
// D-310 S4: contractor は patrol モジュール(クレサポ=巡回・在店集金・報告)に追加。
// D-312 S1: contractor に collection モジュールも開放(マネサポ集金タブ)。店スコープは RLS で既に効く(UI 絞り込み不要)。
//   既存4ロール(admin/manager/patrol/staff)の割り当ては 1 つも変えない(無回帰)。stocktake/admin へは足さない。
export const MODULE_ACCESS = {
  patrol:    ['admin', 'manager', 'patrol', 'staff', 'contractor'],
  collection:['admin', 'manager', 'contractor'],
  sales:     ['admin', 'manager'],
  arrivals:  ['admin', 'manager'],
  // SPEC-STOCKTAKE-STAFF-ACCESS-V1 (D-576): 社員アルバイト(staff)にタナサポを開放。
  //   ★実体(/stock/stocktake → /stock/hub → 4 タスクカード)は元から全て ProtectedRoute で、
  //     StocktakeTargetPage にも staff 分岐(自分の担当分だけ見せる)が実装済みだった。
  //     唯一の詰まりがここで、getModuleTilesForRole が Launcher のタイルを落としていた。
  //   ★patrol / contractor は足さない(ひろの指示は社員アルバイトのみ)。
  //   ★管理側の棚卸し画面(/admin/stocktake)は ManagerRoute のままで staff には開いていない。
  stocktake: ['admin', 'manager', 'staff'],
  orders:    ['admin', 'manager'],
  admin:     ['admin'],
}

/** ランチャー・モジュールタイル（ロールで表示切替）
 *  icon: Lucide の名前(D-1013, ひろ選択)。描くときは shared/ui/icons/HubIcons.jsx の HubIcon で引く。 */
export const LAUNCHER_MODULE_TILES = [
  {
    key: 'clawsupport',
    label: 'クレサポ',
    icon: 'route',
    desc: '巡回・店舗メーター入力',
    path: '/clawsupport',
    requiredModules: ['patrol'],
  },
  {
    key: 'tanasupport',
    label: 'タナサポ',
    icon: 'package-check',
    desc: '棚卸し・入荷チェック',
    // J-STOCK-LAUNCHER-REDIRECT-01 (5/31): /tanasupport → /stock に変更
    // J-STOCK-LAUNCHER-REDIRECT-02 (6/1): /stock → /stock/stocktake に変更し、
    // StocktakeTargetPage (倉庫/担当 2 タブ) に直行。
    path: '/stock/stocktake',
    requiredModules: ['stocktake'],
  },
  {
    key: 'manesupport',
    label: 'マネサポ',
    icon: 'layout-dashboard',
    desc: '管理・分析',
    path: '/admin',
    requiredModules: ['admin'],
  },
]

/** 未実装モジュール（全ロールで disabled 表示） */
export const LAUNCHER_COMING_SOON_TILES = [
  {
    key: 'm3_sales',
    label: '売上集計',
    icon: 'chart-column',
    desc: 'M3 · Coming Soon',
  },
  {
    key: 'm4_master',
    label: 'マスタ参照',
    icon: 'book-open',
    desc: 'M4 · Coming Soon',
  },
]

export function canAccess(role, moduleKey) {
  if (!role) return false
  if (role === ROLES.ADMIN) return true
  return MODULE_ACCESS[moduleKey]?.includes(role) ?? false
}

// ============================================================
// SPEC-MANESUPPORT-ROLE-TABS-01 (D-140): マネサポのタブをロール別に出し分ける「単一の真実」。
//   ランチャータイル(R7) / タブ描画(R2) / 入口ガード(R6) / ルートガード(R4) の4か所すべてがこれを参照する。
//   グレーアウトは採らない(見えないものは触りたくならない=ひろ判断)。押せないボタンは問い合わせを生むだけ。
// ============================================================

// タブ定義(key/ラベル/パス と 並び順)。R2 の並び: 分析 店舗 集金 管理 各履歴 マスタ 設定。
//   ★D-1034: 「QRラベル」タブ(/admin/labels)は運用で使っていないので撤去(ひろ 2026-10-10)。/admin/labels は App.jsx で /admin へ転送。
//   ★D-866: 「店舗」(日報・月報)を分析の隣に新設(ひろ指示: 分析タブはメニューが多いので新規タブに分ける)。
export const ADMIN_TABS = [
  { key: 'reports',    label: '分析',     path: '/admin/reports'    },
  { key: 'store',      label: '店舗',     path: '/admin/store'      },
  { key: 'collection', label: '集金',     path: '/admin/collection' },
  { key: 'manage',     label: '管理',     path: '/admin/manage'     },
  { key: 'audit',      label: '各履歴',   path: '/admin/audit'      },
  { key: 'masters',    label: 'マスタ',   path: '/admin/masters'    },
  { key: 'settings',   label: '設定',     path: '/admin/settings'   },
]

// R1: ロール → 可視タブ の対応表(この1つが唯一の真実。重複定義を作らない)。
//   ★D-866: admin/manager は無条件に 'store' を持つ(元々全店を見られるので店ごとの分岐は不要)。
//     staff は引き続き空のまま(店に紐づくかどうかで決まる例外は下の CONDITIONAL_ROLE_TABS へ分離し、
//     「ロールだけで決まる」というこの表自体の性質は壊さない)。
export const ROLE_VISIBLE_TABS = {
  admin:   ['reports', 'store', 'collection', 'audit', 'masters', 'settings', 'manage'],
  manager: ['reports', 'store', 'collection', 'manage'],
  patrol:  ['reports'],
  staff:   [],
  // D-310 S4: contractor は当初マネサポ非表示だった。
  // D-312 S1: 集金タブのみ開放。可視タブ=collection のみ → マネサポタップで /admin/collection へ自動着地(AdminLayout index)。
  contractor: ['collection'],
}

// SPEC-STORE-DAILY-REPORT-MODULE-01 (D-866) DECISION_2 / MENU_PICK: 直営 2 店(KRM01/IZK01)のアルバイト
//   (staff)にだけ「店舗」タブ(日報・月報)を開く。店に紐づくかどうかは role だけでは分からないので、
//   ROLE_VISIBLE_TABS(role だけで決まる表)を汚さずここに分離する。
//   ctx = current_staff_direct_stores() の結果(呼び出し側がログイン後に1回だけ取る直営店の配列)。
//   ctx が1件以上のときだけ、対応するタブを追加で見せる。呼ぶ側(4関数)に role 別の if を書かせない。
export const CONDITIONAL_ROLE_TABS = {
  staff: ['store'],
}

function resolvedVisibleTabKeys(role, ctx) {
  const keys = new Set(ROLE_VISIBLE_TABS[role] ?? [])
  if (Array.isArray(ctx) && ctx.length > 0) {
    for (const k of (CONDITIONAL_ROLE_TABS[role] ?? [])) keys.add(k)
  }
  return keys
}

// ロールの可視タブを ADMIN_TABS の並び順で返す(def オブジェクトの配列)。
//   ctx: D-866 の店条件つきタブ判定用(省略可。省略時は従来どおりロールだけで決まる=既存呼び出し元は無回帰)。
export function visibleAdminTabs(role, ctx) {
  const allowed = resolvedVisibleTabKeys(role, ctx)
  return ADMIN_TABS.filter(t => allowed.has(t.key))
}

// R6/R7: 可視タブが1つ以上あるか(入口ガード / ランチャータイル)。
export function hasAnyAdminTab(role, ctx) {
  return resolvedVisibleTabKeys(role, ctx).size > 0
}

// SPEC-ADMIN-TAB-SWIPE-01 (D-881): 「タブを押して着地する画面」かどうか。ADMIN_TABS の path と
//   完全一致するときだけ true(深い画面 = 一覧の先の詳細/フォーム等は対象外)。
//   ★これが唯一の真実。横スワイプでのタブ送りはこれが true の画面だけで有効にする。
export function isAdminHubPath(pathname) {
  return ADMIN_TABS.some(t => t.path === pathname)
}

// R4: ロールが当該タブを見られるか(ルートガード)。
export function canSeeAdminTab(role, tabKey, ctx) {
  return resolvedVisibleTabKeys(role, ctx).has(tabKey)
}

// R4: パス → タブキー。例外2件のみ: /admin/masters/prizes は manage(景品は管理タブへ移すが URL 据置=ブックマーク維持)、
//   /admin/reports/collections は collection(R9: 集金抽出は集金タブ扱い。reports 扱いだと社員が xlsx を直打ちで出せる穴)。
export function tabKeyForAdminPath(pathname) {
  if (!pathname) return null
  if (/^\/admin\/masters\/prizes(\/|$)/.test(pathname)) return 'manage'          // 例外1
  // D-1045: 景品の短縮ルールも景品と同じ扱い(manager 以上が直す。マスタタブは admin しか持たないため manage に寄せる)。
  if (/^\/admin\/masters\/abbrev-rules(\/|$)/.test(pathname)) return 'manage'
  if (/^\/admin\/reports\/collections(\/|$)/.test(pathname)) return 'collection' // 例外2 (R9)
  const PREFIX_TAB = [
    ['/admin/reports',         'reports'],
    // SPEC-STORE-DAILY-REPORT-MODULE-01 (D-866): 店舗タブ(日報・月報)。★末尾でなく reports の直後に置く
    //   (ADMIN_TABS の並びと揃える。前方一致の吸い込みは起きない=他の prefix と重ならない)。
    ['/admin/store',           'store'],
    // D-866 SIDE_FINDING(親D-866 MENU_LIST): /admin/activity と /admin/import は PREFIX_TAB に無く
    //   tabKeyForAdminPath が null を返していた = AdminLayout の R4 を素通りしていた(マネサポのタブを
    //   1つでも持つロールなら URL 直打ちで入れた)。MENU_LIST の分類どおり、活動ログ=各履歴(audit)、
    //   取込=マスタ(masters)に載せて穴を塞ぐ。★admin だけがこの2タブを持つので実効的に admin 限定のまま
    //     (manager/patrol/staff/contractor は audit/masters いずれも可視タブに無い)。
    ['/admin/activity',        'audit'],
    ['/admin/import',          'masters'],
    // D-312 S1b: 集金フラグ編集は設定タブへ退避(admin 専用)。**並び順は変えない**=collection-flag を collection より前に置き続ける
    //   (順を入れ替えると /admin/collection-flag が /admin/collection の前方一致に吸い込まれ settings 判定に届かない)。
    ['/admin/collection-flag', 'settings'],
    ['/admin/collection',      'collection'],
    ['/admin/audit',           'audit'],
    ['/admin/masters',         'masters'],
    ['/admin/settings',        'settings'],
    ['/admin/manage',          'manage'],
    // SPEC-ADMIN-STOCKTAKE-ENTRY-V1 (D-562) ITEM_1: 棚卸しの管理画面は実在するのに
    //   PREFIX_TAB に無く、URL を直打ちしてもタブキーが null だった。manage に載せる。
    //   ★末尾に足すだけ。既存行の順番は 1 つも動かさない(collection-flag は collection より前のまま)。
    //   ★/admin/stocktake は他のどの prefix とも前方一致しないので末尾で安全。
    ['/admin/stocktake',       'manage'],
    // SPEC-OPS-HEALTH-RADAR-V1 (D-574): 経営レーダーも管理タブに載せる。
    //   ★末尾に足すだけ。既存行の順番は 1 つも動かさない。
    //   ★/admin/ops-radar は他のどの prefix とも前方一致しない。
    ['/admin/ops-radar',       'manage'],
    // SPEC-ORDER-DOC-TEXT-EXTRACT-V1 (D-575): 請書の取込状況も管理タブ。★末尾に足すだけ。
    ['/admin/order-ack',       'manage'],
    // SPEC-PATROL-SCHEDULE-BY-STORE-01 (D-710) 三: 巡回予定(担当者別)も管理タブ。
    //   ★末尾に足すだけ。既存行の順番は 1 つも動かさない
    //     (collection-flag は collection より前のまま。順を入れ替えると前方一致に吸い込まれる)。
    //   ★ここに足さないと tabKeyForAdminPath が null を返し、AdminLayout の R4 を素通りする
    //     = 管理タブが見えないロールでも URL 直打ちで入れる(D-707 の b 型をわざわざ作ることになる)。
    ['/admin/patrol-schedule-by-staff', 'manage'],
    // SPEC-PRIZE-PROVISIONAL-REGISTER-V1 (D-785) 三: 仮登録の景品の検収も管理タブ。
    //   ★末尾に足すだけ。既存行の順番は 1 つも動かさない
    //     (collection-flag は collection より前のまま。順を入れ替えると前方一致に吸い込まれる)。
    //   ★/admin/provisional-prizes は他のどの prefix とも前方一致しない。
    ['/admin/provisional-prizes', 'manage'],
    // SPEC-OPS-STATS-SELF-RECOVERY-V1 (D-790) 三: 運用の見張りも管理タブ。
    //   ★末尾に足すだけ。既存行の順番は 1 つも動かさない。
    //   ★/admin/ops-watch は他のどの prefix とも前方一致しない
    //     (/admin/ops-radar とは 'ops-' の後が違うので吸い込まれない)。
    ['/admin/ops-watch', 'manage'],
    // SPEC-MONTHLY-TARGET-DRAFT-BOOTH-MEDIAN-V1 (D-795): 月次目標の叩き台は **分析タブ**。
    //   ★既存の「月次目標」(/admin/reports/monthly-target)の隣に並ぶものなので reports に載せる。
    //   ★末尾に足すだけ。既存行の順番は 1 つも動かさない
    //     (collection-flag は collection より前のまま。順を入れ替えると前方一致に吸い込まれる)。
    //   ★/admin/target-draft は他のどの prefix とも前方一致しない。
    //   ★ここに足さないと tabKeyForAdminPath が null を返し、AdminLayout の R4 を素通りする。
    ['/admin/target-draft', 'reports'],
    // SPEC-IN-APP-FEEDBACK-01 (D-852) AC5 follow-up: 現場からの 不具合・要望 一覧も管理タブ。
    //   ★末尾に足すだけ。既存行の順番は 1 つも動かさない
    //     (collection-flag は collection より前のまま。順を入れ替えると前方一致に吸い込まれる)。
    //   ★/admin/feedback は他のどの prefix とも前方一致しない。
    //   ★ここに足さないと tabKeyForAdminPath が null を返し、AdminLayout の R4 を素通りする。
    ['/admin/feedback', 'manage'],
    // SPEC-MACHINE-INVENTORY-V1 (D-904): 機械内在庫一覧も管理タブ。
    //   ★末尾に足すだけ。既存行の順番は 1 つも動かさない
    //     (collection-flag は collection より前のまま。順を入れ替えると前方一致に吸い込まれる)。
    //   ★/admin/machine-inventory は他のどの prefix とも前方一致しない。
    //   ★ここに足さないと tabKeyForAdminPath が null を返し、AdminLayout の R4 を素通りする。
    ['/admin/machine-inventory', 'manage'],
  ]
  for (const [prefix, key] of PREFIX_TAB) {
    if (pathname === prefix || pathname.startsWith(prefix + '/')) return key
  }
  return null
}

// ctx: D-866 の店条件つきタブ判定用(省略可)。直営店に紐づくバイトはこれが 1 件以上あるときだけ
//   マネサポタイルが「店舗」タブ経由で出る。
export function getModuleTilesForRole(role, ctx) {
  return LAUNCHER_MODULE_TILES.filter(tile => {
    // R7: マネサポは「可視タブが1つ以上あるか」で出し分け(社員=分析1つ→表示 / アルバイト=0→非表示、
    //   直営店のアルバイトは店舗タブ経由で表示)。クレサポ・タナサポのタイル条件は変更しない。
    if (tile.key === 'manesupport') return hasAnyAdminTab(role, ctx)
    return tile.requiredModules.some(m => canAccess(role, m))
  })
}
